<template>
    <div class="ls-flow--container">
        <LsTemplatedLoading :show="!fullyLoaded" :full-screen="true" test-id="loading" />
        <div v-if="activeSortedComments.length" class="ls-flow--title">Service Notes</div>
        <form
            v-if="fullyLoaded"
            test-id="form_wrapper"
            class="ls-service-notes-view--form"
            novalidate
            @submit.prevent="createScheduleComment"
        >
            <div
                v-if="activeSortedComments.length"
                ref="commentsContainer"
                class="ls-service-notes-view--comments-container"
            >
                <div
                    v-for="(comment, idx) in activeSortedComments"
                    :key="comment.id"
                    ref="comment"
                    class="ls-service-notes-view--comments"
                >
                    <LsComment
                        :comment="comment"
                        :is-last="activeSortedComments.length === idx + 1"
                    />
                </div>
            </div>

            <LsContentPlaceholder
                v-else
                message="There are no notes yet for this service"
                image="inactive"
                fit-to-parent
                class="ls-service-notes-view--placeholder"
            />

            <div class="ls-service-notes-view--dashed" />

            <LsInput
                v-model="text"
                placeholder="Add note here"
                type="textarea"
                track-id="service_notes_view-text"
                name="text"
                maxlength="1000"
            />
            <LsCheckboxes
                v-model="is_internal"
                track-id="service_notes_view-is_internal"
                name="is_internal"
                :options="[
                    {
                        value: false,
                        label: 'Hide from provider',
                    },
                ]"
            />

            <LsButton
                track-id="service_notes_view-add_note"
                text="Add note"
                :loading="isUpdating"
                type="submit"
                :disabled="!text"
            />
        </form>
    </div>
</template>

<script>
import _ from 'lodash';
import moment from 'moment-timezone';
import { lsErrorService } from 'ls-vue-common';
import { mapActions, mapGetters, mapState } from 'vuex';
import VueScrollTo from 'vue-scrollto';
import { LTRS__COMPLETED_BY_SCHEDULE, LTRS__GET_SCHEDULE_EVENTS } from '@/store/types';
import LsComment from '@/components/LsComment/LsComment.vue';
import { LsButton, LsInput, LsCheckboxes } from '@/components/common';
import LsTemplatedLoading from '@/components/LsTemplatedLoading/LsTemplatedLoading.vue';
import LsContentPlaceholder from '@/components/LsContentPlaceholder/LsContentPlaceholder.vue';
import ScheduleComments from '@/graphql/queries/ScheduleComments.gql';
import CreateScheduleComment from '@/graphql/mutations/CreateScheduleComment.gql';
import { lsHelpers } from '@/helpers';
import { apolloClient } from '@/vue-apollo';

export default {
    name: 'ServiceNotesView',
    components: {
        LsComment,
        LsTemplatedLoading,
        LsButton,
        LsInput,
        LsCheckboxes,
        LsContentPlaceholder,
    },
    data() {
        return {
            loading: 0,
            isUpdating: false,

            text: '',
            is_internal: [],

            variables: {
                customer_id: _.toNumber(this.$route.params.customer_id),
                property_id: _.toNumber(this.$route.params.property_id),
                schedule_id: _.toNumber(this.$route.params.schedule_id),
            },

            isLoadingPartnersData: true,
            partnerEvents: [],
        };
    },
    computed: {
        ...mapState(['ltrs']),
        ...mapGetters([LTRS__COMPLETED_BY_SCHEDULE]),
        activeSortedComments() {
            const comments =
                (this.scheduleComments &&
                    this.scheduleComments.filter(({ deleted_at }) => {
                        return !deleted_at;
                    })) ||
                [];

            const partnerNotes = this.partnerEvents.map((partnerEvent, idx) => {
                return this.convertScheduleEventToNote(partnerEvent, idx);
            });

            return _.sortBy(comments.concat(partnerNotes), 'updated_at');
        },
        fullyLoaded() {
            return !this.loading && !this.isLoadingPartnersData;
        },
    },
    apollo: {
        scheduleComments: {
            query: ScheduleComments,
            variables() {
                return this.variables;
            },
            error(error) {
                lsErrorService.log({ error, showAlert: true });
                this.$apollo.queries.scheduleComments.skip = true;
                lsHelpers.goToParentView({ replace: true });
            },
            loadingKey: 'loading',
        },
    },
    async created() {
        await this[LTRS__GET_SCHEDULE_EVENTS]({
            customerId: _.toNumber(this.variables.customer_id),
        });
        const ltrsGroupedBySchedule = this[LTRS__COMPLETED_BY_SCHEDULE];
        const scheduleId = this.variables.schedule_id;
        this.partnerEvents = (ltrsGroupedBySchedule && ltrsGroupedBySchedule[scheduleId]) || [];
        this.isLoadingPartnersData = false;
    },
    methods: {
        ...mapActions([LTRS__GET_SCHEDULE_EVENTS]),
        convertScheduleEventToNote(event, idx) {
            const completedAt =
                (event.completedAt && moment(event.completedAt).format('M/D/YYYY')) || 'N/A';
            const technician = (event.technician && event.technician.name) || 'N/A';
            const note = {
                id: event.lsApiScheduleEventId + '-' + idx,
                author: {},
                is_internal: true,
                is_partners_portal: true,
                text: `Service Completed: ${completedAt}\n\nProvider Notes:\n${event.notes ||
                    'N/A'}\n\nProduct Applied: ${event.productsUsed ||
                    'N/A'}\n\nTechnician Name: ${technician}`,
                updated_at: event.updatedAt,
            };
            return note;
        },
        createScheduleComment() {
            this.isUpdating = true;
            this.$apollo
                .mutate({
                    mutation: CreateScheduleComment,
                    variables: {
                        ...this.variables,
                        is_internal: Boolean(_.size(this.is_internal)),
                        text: this.text,
                    },
                })
                .then(({ data }) => {
                    this.text = '';
                    this.is_internal = [];
                    apolloClient.writeQuery({
                        query: ScheduleComments,
                        variables: this.variables,
                        data: {
                            scheduleComments: _.concat(
                                this.scheduleComments,
                                data.createScheduleComment
                            ),
                        },
                    });

                    // $nextTick to ensure dom has been updated with new comment
                    this.$nextTick(() => {
                        VueScrollTo.scrollTo(_.last(this.$refs.comment), 500, {
                            container: this.$refs.commentsContainer,
                        });
                    });
                })
                .catch((error) => {
                    lsErrorService.log({ error, showAlert: true });
                })
                .finally(() => {
                    this.isUpdating = false;
                });
        },
    },
};
</script>
<style lang="scss">
.ls-service-notes-view--form {
    .textarea:not([rows]) {
        min-height: 58px;
    }
}

.ls-service-notes-view--dashed {
    border-top: 1px dashed $primary;
}

.ls-service-notes-view--comments-container {
    margin-bottom: 0 !important;
    height: 420px;
    overflow: auto;
    width: 330px;
    @media (min-width: $desktop) {
        width: 720px;
    }
}

.ls-service-notes-view--comments {
    margin-bottom: 15px;
}

.ls-service-notes-view--placeholder {
    margin-top: 50px;
    padding-bottom: 95px !important;
}
</style>
